import * as AgGrid from "ag-grid-community";
import * as http from "../utils/http";
import * as notifications from "utils/notifications";

const LIMIT_PARAM = "limit";
const PAGE_PARAM = "page";
export const SEARCH_PARAM = "search";
const SORT_PARAM = "sort";
const DIRECTION_PARAM = "direction";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function cloneURL(url: URL) {
  return new URL(url.toString());
}

function mergeURLs(sourceUrl: URL, originalTargetUrl: URL, key: string) {
  const targetUrl = cloneURL(originalTargetUrl);
  targetUrl.searchParams.delete(key);
  sourceUrl.searchParams.getAll(key).forEach((value) => targetUrl.searchParams.append(key, value));
  return targetUrl;
}

export function replaceSortParams(sourceUrl: URL, targetUrl: URL) {
  targetUrl = mergeURLs(sourceUrl, targetUrl, SORT_PARAM);
  targetUrl = mergeURLs(sourceUrl, targetUrl, DIRECTION_PARAM);
  return targetUrl;
}

export interface DatasourceCallbacks {
  getSearchInput?: () => string | undefined;
  /** Mutate the request URL before fetch (e.g. add custom query params). */
  appendQueryParams?: (requestUrl: URL) => void;
  onDataReceivedSuccess?: (data: { records: any[] }) => void;
  onDataRequested?: (requestUrl: URL) => void;
}

type Primitive = string | number | boolean;

export class Datasource implements AgGrid.IDatasource {
  callbacks: DatasourceCallbacks;
  indexUrl: string;
  limit: number;
  gridApi?: AgGrid.GridApi;
  filterModelJson?: string;
  sortModelJson?: string;
  #skipNextGetRows = false;

  constructor(params: { callbacks: DatasourceCallbacks; indexPath?: string; limit: string }) {
    this.callbacks = params.callbacks;
    this.indexUrl = this.#parseIndexUrl(params.indexPath);
    this.limit = parseInt(params.limit);

    this.filterModelJson = null;
    this.sortModelJson = null;
  }

  async getRows(params: AgGrid.IGetRowsParams) {
    const url = this.#buildUrl(params);

    if (this.#skipNextGetRows) {
      this.#skipNextGetRows = false;
    } else {
      const paymentId = url.searchParams.get("payment_id")?.trim() || "";
      if (paymentId && !UUID_RE.test(paymentId)) {
        const model = this.gridApi?.getFilterModel?.() || {};
        this.#skipNextGetRows = true;
        this.gridApi?.setFilterModel({ ...model, payment_id: null });
        this.gridApi?.purgeInfiniteCache();
        this.#setLoading(false);
        return;
      }
    }

    this.#setLoading(true);
    this.callbacks.onDataRequested?.(cloneURL(url));

    const response = await http.httpGet(url.toString());
    if (response.ok) {
      const json = await response.json();
      params.successCallback(json.records, this.#resolveLastRow(params, json));
      if (!json.records.length) {
        this.#forceNoRowsOverlay();
      }
      this.callbacks.onDataReceivedSuccess?.(json);
    } else {
      notifications.alert(await response.text());
    }

    this.#setLoading(false);

    this.filterModelJson = JSON.stringify(params.filterModel || {});
    this.sortModelJson = JSON.stringify(params.sortModel || []);
  }

  #setLoading(value: boolean) {
    this.gridApi?.setGridOption("loading", value);
  }

  #forceNoRowsOverlay() {
    this.gridApi?.setGridOption("loading", false);
    this.gridApi?.showNoRowsOverlay();
  }

  #buildUrl(params: AgGrid.IGetRowsParams) {
    const url = new URL(this.indexUrl);

    if (this.#parseSearchText()) {
      url.searchParams.append(SEARCH_PARAM, this.#parseSearchText());
    }

    this.#applyFilters(url, params.filterModel);
    this.#applySort(url, params.sortModel);
    this.callbacks.appendQueryParams?.(url);

    url.searchParams.set(LIMIT_PARAM, this.limit.toString());
    url.searchParams.set(PAGE_PARAM, (this.#isModelChanged(params) ? 1 : this.#nextPage(params)).toString());

    return url;
  }

  #applySort(url: URL, sortModel: AgGrid.SortModelItem[]) {
    url.searchParams.delete(SORT_PARAM);
    url.searchParams.delete(DIRECTION_PARAM);

    const first = sortModel?.[0];
    if (!first?.colId || !first.sort) return;

    url.searchParams.set(SORT_PARAM, first.colId);
    url.searchParams.set(DIRECTION_PARAM, first.sort);
  }

  #applyFilters(url: URL, filterModel: AgGrid.FilterModel) {
    Object.entries(filterModel || {}).forEach(([field, raw]) => {
      if (!raw) return;
      this.#deleteExistingFilterParams(url, field);
      this.#setFilterParams(url, field, raw as any);
    });
  }

  #deleteExistingFilterParams(url: URL, field: string) {
    const keysToDelete: string[] = [
      field,
      `${field}[]`,
      `${field}_min`,
      `${field}_max`,
      `${field}_from`,
      `${field}_to`,
    ];
    keysToDelete.forEach((key) => url.searchParams.delete(key));
  }

  #setFilterParams(url: URL, field: string, data: any) {
    if (data.filterType === "text") {
      const value = (data.filter ?? "").toString().trim();
      if (!value) return;
      url.searchParams.set(field, value);
      return;
    }

    if (data.filterType === "number") {
      const fromValue = this.#normalizePrimitive(data.filter);
      const toValue = this.#normalizePrimitive(data.filterTo);

      switch (data.type) {
        case "inRange":
          if (fromValue != null) url.searchParams.set(`${field}_min`, String(fromValue));
          if (toValue != null) url.searchParams.set(`${field}_max`, String(toValue));
          return;
        case "greaterThan":
        case "greaterThanOrEqual":
          if (fromValue != null) url.searchParams.set(`${field}_min`, String(fromValue));
          return;
        case "lessThan":
        case "lessThanOrEqual":
          if (fromValue != null) url.searchParams.set(`${field}_max`, String(fromValue));
          return;
        case "equals":
        default:
          if (fromValue != null) url.searchParams.set(field, String(fromValue));
          return;
      }
    }

    if (data.filterType === "date") {
      const from = this.#normalizeDateTime((data.dateFrom ?? "").toString().trim(), "start");
      const to = this.#normalizeDateTime((data.dateTo ?? "").toString().trim(), "end");

      if (data.type === "inRange") {
        if (from) url.searchParams.set(`${field}_from`, from);
        if (to) url.searchParams.set(`${field}_to`, to);
        return;
      }

      if (from) url.searchParams.set(`${field}_from`, from);
      return;
    }

    if (data.filterType === "set") {
      const values = Array.isArray(data.values) ? data.values.filter(Boolean) : [];
      if (!values.length) return;
      const key = field === "status" ? `${field}[]` : field;
      values.forEach((value) => url.searchParams.append(key, String(value)));
      return;
    }

    if (typeof data === "string" || typeof data === "number" || typeof data === "boolean") {
      url.searchParams.set(field, String(data));
    }
  }

  #normalizePrimitive(value: unknown): Primitive | null {
    if (value === null || value === undefined) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed.length ? trimmed : null;
    }
    if (typeof value === "number" || typeof value === "boolean") return value;
    return null;
  }

  #normalizeDateTime(value: string, boundary: "start" | "end"): string {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return boundary === "start" ? `${value}T00:00:00` : `${value}T23:59:59`;
    }
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) {
      return value.replace(" ", "T");
    }
    return value;
  }

  #isModelChanged(params: AgGrid.IGetRowsParams) {
    const nextFilterModelJson = JSON.stringify(params.filterModel || {});
    const nextSortModelJson = JSON.stringify(params.sortModel || []);

    return (
      this.filterModelJson != null &&
      this.sortModelJson != null &&
      (this.filterModelJson !== nextFilterModelJson || this.sortModelJson !== nextSortModelJson)
    );
  }

  #nextPage(params: AgGrid.IGetRowsParams) {
    return Math.ceil(params.endRow / this.limit);
  }

  #resolveLastRow(
    params: AgGrid.IGetRowsParams,
    json: { records: any[]; next_page?: number | null; page_count?: number | null },
  ) {
    if (json.next_page) {
      const pageCount = Number(json.page_count);
      return pageCount > 0 ? pageCount * this.limit : -1;
    }

    return (Math.floor(params.endRow / this.limit) - 1) * this.limit + json.records.length;
  }

  #parseIndexUrl(indexPath: string | undefined) {
    return indexPath ? new URL(indexPath, document.location.origin).toString() : document.location.href;
  }

  #parseSearchText() {
    return this.callbacks.getSearchInput?.()?.trim();
  }
}
